import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Checkbox,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import { getMyPermissions } from '../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../utils/permissions';
import {
  addChecksToBatch,
  deleteCheckBatch,
  deleteCheckPayment,
  downloadCheckBatchDepositReport,
  downloadCheckBatchPdf,
  finalizeCheckBatch,
  getCheckBatch,
  getCheckPayments,
  getPendingChecksBySource,
  removeCheckFromBatch,
} from '../../endpoints/financePayments';
import { CHECK_BATCH_LIST_QUERY_KEYS } from './CreateCheckBatchDialog';
import CheckPaymentSourceCell from './CheckPaymentSourceCell';
import type { CheckPaymentDTO } from '../../types/financePayments';
import { CHECK_PAYMENT_SOURCE_LABELS } from '../../utils/checkPaymentSourceLink';

const PICK_PAGE_SIZE = 25;

/** Stable fallback so `pendingRows` is not a new [] each render when data is absent. */
const EMPTY_CHECK_PAYMENTS: CheckPaymentDTO[] = [];

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

function matchesCheckNumberFilter(
  checkNumber: string,
  filter: string
): boolean {
  const f = filter.trim().toLowerCase();
  if (!f) return true;
  return checkNumber.toLowerCase().includes(f);
}

function sourceFilterLabel(source: string): string {
  return CHECK_PAYMENT_SOURCE_LABELS[source] ?? source;
}

const PaymentsCheckBatchDetailPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pickPage, setPickPage] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [checkNumberSearch, setCheckNumberSearch] = useState('');
  const [checkNumberQuery, setCheckNumberQuery] = useState('');
  const [inBatchFilter, setInBatchFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    checkNumber: string;
  } | null>(null);
  const [deleteBatchOpen, setDeleteBatchOpen] = useState(false);

  const { data: myPermissions = [] } = useApiGet(
    ['finance-payments-batch-perms'],
    () => getMyPermissions({ international: '1' }),
    { staleTime: 60_000 }
  );
  const canDeleteCheck = useMemo(
    () => hasUserApplicationPermission(myPermissions, 'checks:delete'),
    [myPermissions]
  );

  const { data, isLoading, refetch } = useApiGet(
    ['finance-payments-batch', batchId],
    () => getCheckBatch(batchId!),
    { enabled: !!batchId, retry: 1 }
  );

  const batch = data?.batch;
  const pickEnabled = !!batchId && batch?.status === 'pending';

  const { data: pendingBySource } = useApiGet(
    ['finance-payments-pending-by-source'],
    getPendingChecksBySource,
    { enabled: pickEnabled, retry: 1 }
  );

  const { data: pickList, refetch: refetchPickList } = useApiGet(
    [
      'finance-payments-pick-list',
      batchId,
      pickPage,
      checkNumberQuery,
      sourceFilter,
    ],
    () =>
      getCheckPayments({
        page: pickPage + 1,
        page_size: PICK_PAGE_SIZE,
        status: 'pending',
        unbatched_only: true,
        check_number: checkNumberQuery || undefined,
        source: sourceFilter || undefined,
      }),
    { enabled: pickEnabled, retry: 1 }
  );

  useEffect(() => {
    setPickPage(0);
    setSelected(new Set());
    setCheckNumberSearch('');
    setCheckNumberQuery('');
    setInBatchFilter('');
    setSourceFilter(searchParams.get('source')?.trim() ?? '');
  }, [batchId, searchParams]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setCheckNumberQuery(checkNumberSearch);
      setPickPage(0);
      setSelected(new Set());
    }, 300);
    return () => window.clearTimeout(t);
  }, [checkNumberSearch]);

  const pendingRows: CheckPaymentDTO[] = pickList?.data ?? EMPTY_CHECK_PAYMENTS;
  const pickTotal = pickList?.pagination?.total_count ?? 0;

  const pageIds = useMemo(() => pendingRows.map((c) => c.id), [pendingRows]);

  const numSelectedOnPage = useMemo(
    () => pageIds.filter((id) => selected.has(id)).length,
    [pageIds, selected]
  );

  const allOnPageSelected =
    pageIds.length > 0 && numSelectedOnPage === pageIds.length;
  const someOnPageSelected =
    numSelectedOnPage > 0 && numSelectedOnPage < pageIds.length;

  const { mutate: addChecks, isPending: adding } = useApiSend(
    (ids: string[]) => addChecksToBatch(batchId!, ids),
    () => {
      setErrorMsg(null);
      setSelected(new Set());
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: removeCheck, isPending: removing } = useApiSend(
    (checkId: string) => removeCheckFromBatch(batchId!, checkId),
    () => {
      setErrorMsg(null);
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: deleteCheck, isPending: deleting } = useApiSend(
    (checkId: string) => deleteCheckPayment(checkId),
    () => {
      setErrorMsg(null);
      const deletedId = deleteTarget?.id;
      setDeleteTarget(null);
      if (deletedId) {
        setSelected((prev) => {
          if (!prev.has(deletedId)) return prev;
          const next = new Set(prev);
          next.delete(deletedId);
          return next;
        });
      }
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: finalizeMutate, isPending: finalizing } = useApiSend(
    () => finalizeCheckBatch(batchId!),
    () => {
      setErrorMsg(null);
      void refetch();
    },
    (err) => setErrorMsg(getApiErrorMessage(err)),
    [...CHECK_BATCH_LIST_QUERY_KEYS]
  );

  const { mutate: deleteBatchMutate, isPending: deletingBatch } = useApiSend(
    () => deleteCheckBatch(batchId!),
    () => {
      setErrorMsg(null);
      setDeleteBatchOpen(false);
      navigate('/finance/payments/check-batches');
    },
    (err) => setErrorMsg(getApiErrorMessage(err)),
    [...CHECK_BATCH_LIST_QUERY_KEYS]
  );

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleAllOnPage = (checked: boolean) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (checked) {
        pageIds.forEach((id) => n.add(id));
      } else {
        pageIds.forEach((id) => n.delete(id));
      }
      return n;
    });
  };

  const handleAddSelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      setErrorMsg('Select at least one check using the checkboxes.');
      return;
    }
    addChecks(ids);
  };

  const handlePrint = async () => {
    try {
      setErrorMsg(null);
      await downloadCheckBatchPdf(batchId!);
    } catch (e) {
      setErrorMsg(getApiErrorMessage(e));
    }
  };

  const handleDepositGlPdf = async () => {
    try {
      setErrorMsg(null);
      await downloadCheckBatchDepositReport(batchId!);
    } catch (e) {
      setErrorMsg(getApiErrorMessage(e));
    }
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteCheck(deleteTarget.id);
    }
  };

  const checks = useMemo(
    () => data?.checks ?? EMPTY_CHECK_PAYMENTS,
    [data?.checks]
  );
  const filteredInBatchChecks = useMemo(
    () =>
      checks.filter((c) =>
        matchesCheckNumberFilter(c.check_number, inBatchFilter)
      ),
    [checks, inBatchFilter]
  );

  const showBatchActions = batch?.status === 'pending';
  const canDeleteEmptyBatch =
    batch?.status === 'pending' && checks.length === 0;
  const selectedCount = selected.size;

  if (!batchId) {
    return <Typography>Missing batch id.</Typography>;
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/finance/payments/check-batches')}
        sx={{ mb: 2 }}
      >
        Back to batches
      </Button>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      {isLoading && <Typography>Loading…</Typography>}

      {batch && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {batch.batch_number}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Status: <strong>{batch.status}</strong> · Checks:{' '}
            {batch.check_count} · Total: {fmtMoney(batch.total_amount)}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handlePrint()}
            >
              Print / download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handleDepositGlPdf()}
            >
              Deposit / GL report (PDF)
            </Button>
            {batch.status === 'pending' && (
              <Button
                color="success"
                variant="contained"
                disabled={finalizing || checks.length === 0}
                onClick={() => finalizeMutate(undefined)}
              >
                Finalize deposit
              </Button>
            )}
            {canDeleteEmptyBatch && (
              <Button
                color="error"
                variant="outlined"
                disabled={deletingBatch}
                onClick={() => setDeleteBatchOpen(true)}
              >
                Delete batch
              </Button>
            )}
          </Box>

          {batch.status === 'pending' && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Add pending checks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select unbatched pending checks (same style as a pick list). Use
                pagination to move through the queue, or search by check number.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 2,
                  alignItems: 'center',
                }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="check-source-filter-label">Source</InputLabel>
                  <Select
                    labelId="check-source-filter-label"
                    label="Source"
                    value={sourceFilter}
                    onChange={(e: SelectChangeEvent<string>) => {
                      setSourceFilter(e.target.value);
                      setPickPage(0);
                      setSelected(new Set());
                    }}
                  >
                    <MenuItem value="">All sources</MenuItem>
                    {(pendingBySource?.items ?? []).map((row) => (
                      <MenuItem key={row.source} value={row.source}>
                        {sourceFilterLabel(row.source)} ({row.count})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Search by check #"
                  value={checkNumberSearch}
                  onChange={(e) => setCheckNumberSearch(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Button
                  variant="contained"
                  disabled={adding || selectedCount === 0}
                  onClick={handleAddSelected}
                >
                  Add selected to batch
                  {selectedCount > 0 ? ` (${selectedCount})` : ''}
                </Button>
                {selectedCount > 0 && (
                  <Button size="small" onClick={() => setSelected(new Set())}>
                    Clear selection
                  </Button>
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected}
                        onChange={(_, checked) => toggleAllOnPage(checked)}
                        inputProps={{
                          'aria-label': 'select all pending on this page',
                        }}
                      />
                    </TableCell>
                    <TableCell>Check #</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Memo / full source</TableCell>
                    <TableCell>ID</TableCell>
                    {canDeleteCheck && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRows.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(c.id)}
                          onChange={() => toggleOne(c.id)}
                          inputProps={{ 'aria-label': `select ${c.id}` }}
                        />
                      </TableCell>
                      <TableCell>{c.check_number}</TableCell>
                      <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                      <TableCell>
                        <CheckPaymentSourceCell
                          checkSource={c.check_source}
                          sourceId={c.source_id}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 280 }}>
                          {(c.check_source ?? '').trim() || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          title={c.id}
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {shortId(c.id)}
                        </Typography>
                      </TableCell>
                      {canDeleteCheck && (
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            disabled={deleting}
                            onClick={() =>
                              setDeleteTarget({
                                id: c.id,
                                checkNumber: c.check_number,
                              })
                            }
                          >
                            Delete
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {pendingRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={canDeleteCheck ? 7 : 6}>
                        {checkNumberQuery
                          ? 'No matching unbatched pending checks.'
                          : 'No unbatched pending checks. New mail-in orders will appear here.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pickTotal}
                page={pickPage}
                onPageChange={(_, p) => setPickPage(p)}
                rowsPerPage={PICK_PAGE_SIZE}
                rowsPerPageOptions={[PICK_PAGE_SIZE]}
                onRowsPerPageChange={() => {}}
              />
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Checks in batch
            </Typography>
            {checks.length > 0 && (
              <TextField
                size="small"
                label="Filter by check #"
                value={inBatchFilter}
                onChange={(e) => setInBatchFilter(e.target.value)}
                sx={{ mb: 2, minWidth: 220 }}
              />
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Check #</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  {showBatchActions && (
                    <TableCell align="right">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInBatchChecks.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.check_number}</TableCell>
                    <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                    <TableCell>{c.status}</TableCell>
                    <TableCell>
                      <CheckPaymentSourceCell
                        checkSource={c.check_source}
                        sourceId={c.source_id}
                      />
                    </TableCell>
                    {showBatchActions && (
                      <TableCell align="right">
                        {c.status === 'received' && (
                          <Button
                            size="small"
                            disabled={removing}
                            onClick={() => removeCheck(c.id)}
                            sx={{ mr: 1 }}
                          >
                            Remove
                          </Button>
                        )}
                        {canDeleteCheck && c.status === 'received' && (
                          <Button
                            size="small"
                            color="error"
                            disabled={deleting}
                            onClick={() =>
                              setDeleteTarget({
                                id: c.id,
                                checkNumber: c.check_number,
                              })
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredInBatchChecks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={showBatchActions ? 5 : 4}>
                      {checks.length === 0
                        ? 'No checks in this batch yet.'
                        : 'No checks match this filter.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Dialog
        open={deleteBatchOpen}
        onClose={() => !deletingBatch && setDeleteBatchOpen(false)}
      >
        <DialogTitle>Delete empty batch?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete batch {batch?.batch_number ?? ''}? This cannot be
            undone. Only empty pending batches can be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteBatchOpen(false)}
            disabled={deletingBatch}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletingBatch}
            onClick={() => deleteBatchMutate(undefined)}
          >
            Delete batch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
      >
        <DialogTitle>Delete check payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete check #{deleteTarget?.checkNumber ?? ''}? This
            removes the check from the queue and cannot be undone. The linked
            order (if any) is not changed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentsCheckBatchDetailPage;
